/**
 * CheckInForm Component
 * Form for entering task description after photo capture
 */

import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../../components/ui/Button';
import Icon from '../../../../components/AppIcon';

// Predefined task tags for quick selection
const TASK_TAGS = [
  { id: 'email', label: 'Email', icon: 'mail' },
  { id: 'meeting', label: 'Meeting', icon: 'users' },
  { id: 'research', label: 'Research', icon: 'search' },
  { id: 'admin', label: 'Admin', icon: 'clipboard' },
  { id: 'social-media', label: 'Social Media', icon: 'share-2' },
  { id: 'data-entry', label: 'Data Entry', icon: 'database' },
  { id: 'customer-support', label: 'Support', icon: 'message-circle' },
  { id: 'scheduling', label: 'Scheduling', icon: 'calendar' },
  { id: 'bookkeeping', label: 'Bookkeeping', icon: 'dollar-sign' },
  { id: 'other', label: 'Other', icon: 'more-horizontal' }
];

const CheckInForm = ({ photoPreview, onSubmit, onCancel, isLoading }) => {
  const [taskDescription, setTaskDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [characterCount, setCharacterCount] = useState(0);
  const textareaRef = useRef(null);

  const MAX_CHARS = 500;
  const MIN_CHARS = 10;

  // Focus textarea on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARS) {
      setTaskDescription(value);
      setCharacterCount(value.length);
    }
  };

  const handleTagToggle = (tagId) => {
    setSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      }
      return [...prev, tagId];
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (taskDescription.trim().length < MIN_CHARS) {
      return;
    }

    onSubmit(taskDescription.trim(), selectedTags);
  };

  const isValid = taskDescription.trim().length >= MIN_CHARS;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-primary-600 px-6 py-4 flex items-center justify-between sticky top-0">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Icon name="edit-3" className="h-5 w-5" />
            Describe Your Work
          </h3>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="text-white/80 hover:text-white transition-colors disabled:opacity-50"
          >
            <Icon name="x" className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Photo Preview */}
          <div className="mb-6">
            <div className="relative aspect-video max-h-48 rounded-xl overflow-hidden bg-gray-900">
              <img
                src={photoPreview}
                alt="Check-in photo"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded text-xs text-white">
                Check-in Photo
              </div>
            </div>
          </div>

          {/* Quick Tags */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Quick Tags (Optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {TASK_TAGS.map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleTagToggle(tag.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedTags.includes(tag.id)
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Icon name={tag.icon} className="h-3.5 w-3.5" />
                  {tag.label}
                </button>
              ))}
            </div>
          </div>

          {/* Task Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              What have you been working on? <span className="text-red-500">*</span>
            </label>
            <textarea
              ref={textareaRef}
              value={taskDescription}
              onChange={handleDescriptionChange}
              placeholder="Describe the tasks you completed during this check-in period... (e.g., 'Responded to 15 customer emails, updated client spreadsheet with new orders')"
              className={`w-full p-4 border rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none ${
                characterCount > 0 && characterCount < MIN_CHARS
                  ? 'border-yellow-400 dark:border-yellow-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              rows={4}
              disabled={isLoading}
            />
            <div className="flex justify-between mt-2 text-sm">
              <span className={`${
                characterCount > 0 && characterCount < MIN_CHARS
                  ? 'text-yellow-600 dark:text-yellow-400'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                {characterCount < MIN_CHARS 
                  ? `Minimum ${MIN_CHARS} characters required (${MIN_CHARS - characterCount} more)`
                  : 'Good description!'
                }
              </span>
              <span className={`${
                characterCount > MAX_CHARS * 0.9
                  ? 'text-yellow-600 dark:text-yellow-400'
                  : 'text-gray-500 dark:text-gray-400'
              }`}>
                {characterCount}/{MAX_CHARS}
              </span>
            </div>
          </div>

          {/* Tips */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
              <Icon name="info" className="h-4 w-4" />
              Tips for a Good Check-in
            </h4>
            <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
              <li>• Be specific about what you accomplished</li>
              <li>• Include numbers when possible (emails sent, tasks completed)</li>
              <li>• Note any challenges or blockers you encountered</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={onCancel}
              variant="secondary"
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!isValid || isLoading}
              className="flex-1"
            >
              {isLoading ? (
                <>
                  <Icon name="spinner" className="animate-spin h-4 w-4 mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Icon name="check" className="h-4 w-4 mr-2" />
                  Complete Check-in
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckInForm;
